
import React from "react";
import { CountyCollapsibleSection } from "./CountyCollapsibleSection";
import { LoadingState } from "@/components/nursing-homes/LoadingState";
import { ErrorState } from "@/components/nursing-homes/ErrorState";
import { EmptyState } from "@/components/nursing-homes/EmptyState";
import { ResultsSummary } from "./ResultsSummary";

interface CountyListingsProps {
  isLoading: boolean;
  error: Error | null;
  nursingHomesByCounty: Record<string, any[]>;
  countyNames: string[];
  expandedCounties: string[];
  toggleCountyExpansion: (county: string) => void;
  allNursingHomes: {
    data: any[];
    count: number;
  } | undefined;
  searchQuery: string;
  selectedCounty: string | null;
  counties: {
    county: string;
    county_slug: string;
  }[] | undefined;
}

export function CountyListings({
  isLoading,
  error,
  nursingHomesByCounty,
  countyNames,
  expandedCounties,
  toggleCountyExpansion,
  allNursingHomes,
  searchQuery,
  selectedCounty,
  counties
}: CountyListingsProps) {
  if (isLoading) {
    return <LoadingState />;
  }
  
  if (error) {
    return <ErrorState countyName="Ireland" error={error} />;
  }
  
  if (!allNursingHomes || allNursingHomes.data.length === 0) {
    return <EmptyState countyName={selectedCounty ? counties?.find(c => c.county_slug === selectedCounty)?.county || 'selected county' : 'Ireland'} />;
  }

  const filteredCount = Object.values(nursingHomesByCounty).reduce((sum, homes) => sum + homes.length, 0);

  return (
    <>
      <ResultsSummary 
        count={filteredCount} 
        searchQuery={searchQuery} 
        selectedCounty={selectedCounty} 
        counties={counties} 
      />
      
      <div className="space-y-8">
        {countyNames.map(county => {
          const homesInCounty = nursingHomesByCounty[county];
          if (!homesInCounty || homesInCounty.length === 0) return null;
          
          // If filtering by county, only show that county and expand it
          if (selectedCounty) {
            const currentCountyData = counties?.find(c => c.county_slug === selectedCounty);
            if (currentCountyData && currentCountyData.county !== county) return null;
          }
          
          const isExpanded = expandedCounties.includes(county);
          
          return (
            <CountyCollapsibleSection 
              key={county}
              county={county}
              homes={homesInCounty}
              isExpanded={isExpanded}
              toggleCountyExpansion={toggleCountyExpansion}
            />
          );
        })}
      </div>
    </>
  );
}
